import React, { useState, useEffect, useRef } from 'react';
import { Popover, Box, TextField, Typography } from '@mui/material';
import { UnfoldMore } from '@mui/icons-material';

import { isValidHex, hexToHsv, hsvToHex, hexToRgb, rgbToHex } from '../../shared/colors';

const CustomColorPopover = ({ open, anchorEl, onClose, colour, onChange }) => {
  const [localColor, setLocalColor] = useState(colour || '#ffffff');
  const [hsv, setHsv] = useState({ h: 0, s: 0, v: 100 });
  const [format, setFormat] = useState('HEX');
  const [rgbInputs, setRgbInputs] = useState({ r: '255', g: '255', b: '255' });
  const svBoxRef = useRef(null);
  const hueSliderRef = useRef(null);

  // Keep HSV in sync with colour prop when popover opens or colour changes externally
  useEffect(() => {
    if (colour && isValidHex(colour)) {
      setLocalColor(colour);
      setHsv(hexToHsv(colour));
      const { r, g, b } = hexToRgb(colour);
      setRgbInputs({ r: String(r), g: String(g), b: String(b) });
    }
  }, [colour, open]);

  const handleTextChange = (e) => {
    let val = e.target.value;
    if (val && !val.startsWith('#') && /^[0-9A-Fa-f]/i.test(val)) {
      val = '#' + val;
    }
    setLocalColor(val);
    if (isValidHex(val)) {
      setHsv(hexToHsv(val));
      const { r, g, b } = hexToRgb(val);
      setRgbInputs({ r: String(r), g: String(g), b: String(b) });
      onChange(val);
    }
  };

  const handleSvMove = (e) => {
    if (!svBoxRef.current) return;
    const rect = svBoxRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;

    x = Math.max(0, Math.min(rect.width, x));
    y = Math.max(0, Math.min(rect.height, y));

    const s = Math.round((x / rect.width) * 100);
    const v = Math.round((1 - y / rect.height) * 100);

    const newHex = hsvToHex(hsv.h, s, v);
    setLocalColor(newHex);
    setHsv(prev => ({ ...prev, s, v }));
    const { r, g, b } = hexToRgb(newHex);
    setRgbInputs({ r: String(r), g: String(g), b: String(b) });
    onChange(newHex);
  };

  const handleSvMouseDown = (e) => {
    e.preventDefault();
    handleSvMove(e);
    const onMouseMove = (moveEvent) => handleSvMove(moveEvent);
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleHueMove = (e) => {
    if (!hueSliderRef.current) return;
    const rect = hueSliderRef.current.getBoundingClientRect();
    let x = e.clientX - rect.left;

    x = Math.max(0, Math.min(rect.width, x));

    const h = Math.round((x / rect.width) * 360);

    const newHex = hsvToHex(h, hsv.s, hsv.v);
    setLocalColor(newHex);
    setHsv(prev => ({ ...prev, h }));
    const { r, g, b } = hexToRgb(newHex);
    setRgbInputs({ r: String(r), g: String(g), b: String(b) });
    onChange(newHex);
  };

  const handleHueMouseDown = (e) => {
    e.preventDefault();
    handleHueMove(e);
    const onMouseMove = (moveEvent) => handleHueMove(moveEvent);
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleRgbInputChange = (key, value) => {
    const cleaned = value.replace(/[^0-9]/g, '').slice(0, 3);
    const newRgbInputs = { ...rgbInputs, [key]: cleaned };
    setRgbInputs(newRgbInputs);

    const rVal = newRgbInputs.r === '' ? 0 : Math.min(255, parseInt(newRgbInputs.r, 10));
    const gVal = newRgbInputs.g === '' ? 0 : Math.min(255, parseInt(newRgbInputs.g, 10));
    const bVal = newRgbInputs.b === '' ? 0 : Math.min(255, parseInt(newRgbInputs.b, 10));

    const newHex = rgbToHex(rVal, gVal, bVal);
    setLocalColor(newHex);
    setHsv(hexToHsv(newHex));
    onChange(newHex);
  };

  const handleRgbBlur = (key) => {
    if (rgbInputs[key] === '') {
      const { r, g, b } = hexToRgb(localColor);
      setRgbInputs({ r: String(r), g: String(g), b: String(b) });
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{ zIndex: 9999 }}
      PaperProps={{
        sx: {
          p: 1.5,
          width: 240,
          borderRadius: 3,
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          overflow: 'hidden'
        }
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Box display="flex" flexDirection="column" gap={1.5}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: '0.05em' }}>
          CUSTOM COLOR
        </Typography>

        {/* 2D Saturation-Value Box */}
        <Box
          ref={svBoxRef}
          onMouseDown={handleSvMouseDown}
          sx={{
            height: 120,
            width: '100%',
            borderRadius: '6px',
            position: 'relative',
            cursor: 'crosshair',
            backgroundColor: `hsl(${hsv.h}, 100%, 50%)`,
            backgroundImage: `
              linear-gradient(to top, #000, transparent),
              linear-gradient(to right, #fff, transparent)
            `,
            overflow: 'hidden',
            boxShadow: 'inset 0 0 1px rgba(0,0,0,0.2)'
          }}
        >
          {/* Slider indicator */}
          <Box
            sx={{
              position: 'absolute',
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: '2px solid #fff',
              boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              transform: 'translate(-6px, -6px)',
              left: `${hsv.s}%`,
              top: `${100 - hsv.v}%`,
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* 1D Hue Slider */}
        <Box
          ref={hueSliderRef}
          onMouseDown={handleHueMouseDown}
          sx={{
            height: 12,
            width: '100%',
            borderRadius: '6px',
            position: 'relative',
            cursor: 'pointer',
            background: 'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)'
          }}
        >
          {/* Hue slider indicator */}
          <Box
            sx={{
              position: 'absolute',
              width: 8,
              height: '100%',
              borderRadius: '4px',
              border: '1.5px solid #fff',
              backgroundColor: 'transparent',
              boxShadow: '0 0 2px rgba(0,0,0,0.5)',
              transform: 'translateX(-4px)',
              left: `${(hsv.h / 360) * 100}%`,
              pointerEvents: 'none'
            }}
          />
        </Box>

        {/* Input Field & Preview */}
        <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
          {/* Format Toggle Button */}
          <Box
            onClick={() => setFormat(prev => prev === 'HEX' ? 'RGB' : 'HEX')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              height: 32,
              px: 0.75,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              '&:hover': {
                bgcolor: 'action.selected'
              }
            }}
          >
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {format}
            </Typography>
            <UnfoldMore sx={{ fontSize: '0.85rem', color: 'text.secondary', ml: 0.25 }} />
          </Box>

          {format === 'HEX' ? (
            <TextField
              size="small"
              value={localColor}
              onChange={handleTextChange}
              placeholder="#FFFFFF"
              inputProps={{ maxLength: 7 }}
              error={localColor.length > 0 && !isValidHex(localColor)}
              helperText={localColor.length > 0 && !isValidHex(localColor) ? "Invalid HEX" : ""}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  height: 32,
                  borderRadius: '6px',
                },
                '& .MuiInputBase-input': {
                  py: 0.5,
                  px: 1,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  textAlign: 'center'
                },
                '& .MuiFormHelperText-root': {
                  margin: 0,
                  fontSize: '0.65rem',
                  position: 'absolute',
                  bottom: -16,
                  left: 0,
                  whiteSpace: 'nowrap'
                }
              }}
            />
          ) : (
            <Box display="flex" gap={0.5} sx={{ flex: 1 }}>
              {['r', 'g', 'b'].map((channel) => (
                <TextField
                  key={channel}
                  size="small"
                  value={rgbInputs[channel]}
                  onChange={(e) => handleRgbInputChange(channel, e.target.value)}
                  onBlur={() => handleRgbBlur(channel)}
                  placeholder={channel.toUpperCase()}
                  inputProps={{ style: { textAlign: 'center' } }}
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-root': {
                      height: 32,
                      borderRadius: '6px',
                    },
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      px: 0.5,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace'
                    }
                  }}
                />
              ))}
            </Box>
          )}

          {/* Color preview circle */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: `1.5px solid ${isValidHex(localColor) ? '#eee' : '#ccc'}`,
              bgcolor: isValidHex(localColor) ? localColor : '#ffffff',
              flexShrink: 0,
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
            }}
          />
        </Box>
      </Box>
    </Popover>
  );
};

export default CustomColorPopover;
